<template>
  <div>
    <div
      class="d2c-journey__container d2c-quotation-result__container"
      v-if="hasQuotes"
    >
      <div class="white rounded-lg elevation-3 pa-4 pa-xl-16">
        <BaseButton
          :text="LABEL_CONSTANTS.BACK_TO_YOURS_COVER"
          flat
          ripple
          class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-sm-5"
          icon="mdi-chevron-left"
          color="#106175"
          small
          @click="backToYourCover"
        />
        <v-row class="d2c-quotation-result__header">
          <v-col
            class="d-flex flex-column flex-md-row justify-space-between section-title"
          >
            <div>
              <h1
                class="d-block d-lg-none font-28 font-weight-bold black2-color"
              >
                Your quote
              </h1>
              <h1
                class="d-none d-lg-block font-28 font-weight-bold black2-color"
              >
                Your Landlord’s Insurance quote
              </h1>
            </div>
            <div
              class="d-flex flex-column-reverse flex-lg-row align-end align-lg-center d2c-save-exit"
            >
              <BaseButton
                text="View policy documents"
                flat
                depressed
                ripple
                class="font-weight-semi-bold mr-lg-3 py-3 d-flex"
                icon="mdi-file"
                color="#106175"
                small
                @click="viewPolicyDocument()"
              />
              <BaseButton
                text="Save &amp; exit"
                flat
                depressed
                ripple
                class="font-weight-semi-bold py-3 d-flex"
                icon="mdi-content-save"
                color="#106175"
                small
                @click="
                  toggleGlobalSaveAndExitDialog(
                    EXIT_DESTINATION.landlordWelcomePage
                  )
                "
              />
            </div>
          </v-col>
        </v-row>
        <div class="cheapest-flag__container">
          <div class="cheapest-flag">Cheapest!</div>
        </div>
        <TheQuote
          :insurer="theCheapest"
          :cover="getCoverLandlords"
          :quoteExcessConfiguration="
            getQuoteExcessConfiguration?.LandlordsInsurance
          "
          @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
          @continueWithQuote="handleContinueWithQuote"
          @customiseQuote="customiseQuote"
        />
      </div>
      <div
        v-if="morePrices.length"
        :class="[
          !isShowingMorePrices ? 'mb-0' : '',
          'view-more-price__wrapper',
        ]"
      >
        <a
          v-if="isShowingMorePrices"
          @click="isShowingMorePrices = false"
          class="view-link view-less-link"
        >
          <span>View less prices</span>
          <v-icon> mdi-chevron-up </v-icon>
        </a>
        <a
          v-else
          @click="isShowingMorePrices = true"
          class="view-link view-more-link"
        >
          <span>View more prices</span>
          <v-icon> mdi-chevron-down </v-icon>
        </a>
      </div>
      <div
        v-if="morePrices.length && isShowingMorePrices"
        class="more-quote-container"
      >
        <h2 class="more-quote-heading ma-0 my-lg-4 font-28">More quotes</h2>
        <div class="more-quote__wrapper">
          <div
            v-for="(price, index) in morePrices"
            :key="index"
            :class="[index === morePrices.length - 1 ? 'mb-0' : 'mb-15']"
            class="white rounded-lg elevation-3 pa-4 pa-xl-16"
          >
            <TheQuote
              :insurer="price"
              :cover="getCoverLandlords"
              :quoteExcessConfiguration="
                getQuoteExcessConfiguration?.LandlordsInsurance
              "
              @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
              @continueWithQuote="handleContinueWithQuote"
              @customiseQuote="customiseQuote"
            />
          </div>
        </div>
      </div>
    </div>
    <NoQuoteResultPage
      v-else
      @backToYourCover="backToYourCover"
      @customiseQuote="customiseQuote"
    ></NoQuoteResultPage>
    <BaseDialog
      ref="D2CDialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4 d2c-dialog-header"
      contentClass="d2c-breakdown-content"
      dark2Icon
      :isSelfServe="isSelfServe"
    >
      <template v-slot:content="{ data }">
        <D2CDialogContentPremiumBreakdown
          :breakdownModel="data"
        ></D2CDialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="policyDocumentDialog"
      windowClass="pa-0 pb-xl-0"
      headerClass="d2c-dialog-header pt-4 pl-4 pr-4 pb-2 mt-5"
      headerDivider
      :max-width="924"
      :isSelfServe="true"
      dark2Icon
    >
      <template v-slot:content="{}">
        <PolicyDocument
          ref="policyDocumentContent"
          :policyDocumentRows="policyDocumentRows"
        />
      </template>
    </BaseDialog>
    <CustomizeCover
      ref="customizeDrawerInner"
      v-model="isShowDrawerCustomizeCover"
      @closeDrawer="closeDrawer"
    ></CustomizeCover>
  </div>
</template>

<script>
import {
  scrollTop,
  convertToBool,
  resetEligibilityCheck,
} from "@/common/utils";
import { mapActions, mapGetters, mapState } from "vuex";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";

import {
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
} from "@/store/types/module-types";
import { ROUTE_NAME } from "@/constants/route.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { EXIT_DESTINATION } from "@/constants/common.constants";

import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import TheQuote from "@/components/D2CSharedComponents/TheQuote.vue";
import CustomizeCover from "@/components/D2CSharedComponents/CustomizeCover.vue";
import NoQuoteResultPage from "@/components/D2CSharedComponents/NoQuoteResultPage.vue";

export default {
  name: "D2CLandlordQuotationResultPage",
  components: {
    BaseButton,
    TheQuote,
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    D2CDialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/D2CDialogContentPremiumBreakdown.vue"),
    NoQuoteResultPage,
    PolicyDocument: () => import("@/views/Dialog/PolicyDocument.vue"),
    CustomizeCover,
  },
  mixins: [PremiumBreakdownMixin, BuildingsContentsVisibility],
  data() {
    return {
      LABEL_CONSTANTS,
      EXIT_DESTINATION,
      isShowingMorePrices: false,
      selectedQuote: null,
      policyDocumentRows: [],
      isShowDrawerCustomizeCover: false,
      eligibilityData: {
        rentGuarantee: { Value: "" },
        legalExpenses: { Value: "" },
        tenants18OrOver: { Value: "" },
        tenancyAgreement: { Value: "" },
        tenantCreditCheck: { Value: "" },
        firstMonthRentPaid: { Value: "" },
        preGrantNoticesIssued: { Value: "" },
      },
    };
  },
  watch: {
    getQuoteRequestId(value) {
      if (value) {
        this.getDocumentList();
      }
    },
  },
  mounted() {
    scrollTop();
    this.setRestoringLastStep(false);
    if (this.getQuoteRequestId) {
      this.getDocumentList();
    }
    const rentGuarantee = this.questionMap.RentGuarantee;
    if (rentGuarantee) {
      rentGuarantee.Value = convertToBool(rentGuarantee.Value);
    }
    const legalExpenses = this.questionMap.LegalExpenses;
    if (legalExpenses) {
      legalExpenses.Value = convertToBool(legalExpenses.Value);
    }
    this.eligibilityData = {
      rentGuarantee,
      legalExpenses,
      tenants18OrOver: this.questionMap.Tenants18OrOver,
      tenancyAgreement: this.questionMap.TenancyAgreement,
      tenantCreditCheck: this.questionMap.TenantCreditCheck,
      firstMonthRentPaid: this.questionMap.FirstMonthRentPaid,
      preGrantNoticesIssued: this.questionMap.PreGrantNoticesIssued,
    };
  },
  beforeDestroy() {
    document.getElementsByTagName("html")[0].style.overflow = "";
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    ...mapGetters(QUESTIONS_MODULE, [
      "hasQuotes",
      "getCoverLandlords",
      "insurerQuotesModel",
      "isSelfServe",
      "getQuoteRequestId",
      "getQuoteExcessConfiguration",
    ]),
    theCheapest() {
      return this.insurerQuotesModel.find((quote) => quote.isCheapest);
    },
    morePrices() {
      return this.insurerQuotesModel.filter(
        (quote) => !quote.isCheapest && quote.isAvailable
      );
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getD2CQuoteDocument",
      "cloneQuote",
      "setRestoringLastStep",
      "toggleGlobalSaveAndExitDialog",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "setApplicationActiveQuote",
      "updateSelectedQuote",
    ]),
    backToYourCover() {
      // clone to a new quote so that functionality like property lookup doesn't break
      resetEligibilityCheck(this.eligibilityData);
      this.cloneQuote();
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.D2C.COVER_DETAILS });
    },
    viewPolicyDocument() {
      this.$refs.policyDocumentDialog.open("Policy documents");
    },
    handleContinueWithQuote(insurer) {
      this.selectedQuote = insurer.selectedQuote;
      this.proceedWithApply(insurer);
    },
    proceedWithApply(insurer) {
      this.setApplicationActiveQuote(insurer);
      this.updateSelectedQuote();
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.D2C.APPLICATION });
    },
    async getDocumentList() {
      try {
        const documents = await this.getD2CQuoteDocument();
        this.policyDocumentRows = [];
        if (documents["terms"]) {
          this.policyDocumentRows.push({
            ...documents["terms"],
            Name: "Landlord’s Insurance Policy Booklet",
          });
        }
        if (documents["ipid"]) {
          this.policyDocumentRows.push({
            ...documents["ipid"],
            Name: "Landlord’s Insurance Product Information",
          });
        }
      } catch (e) {
        console.error(e);
      }
    },
    customiseQuote() {
      this.isShowDrawerCustomizeCover = true;
      document.getElementsByTagName("html")[0].style.overflow = "hidden";
    },
    closeDrawer() {
      this.isShowDrawerCustomizeCover = false;
      document.getElementsByTagName("html")[0].style.overflow = "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/d2c/journey.scss";

.d2c-journey__container {
  padding: 0 !important;
}

.d2c-quotation-result__container {
  .d2c-quotation-result__header {
    margin-top: -11px;
  }
  .cheapest-flag__container {
    margin: 8px -16px 16px;
    padding-left: 16px;

    .cheapest-flag {
      font-size: 20px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: 0.4px;
      background-color: #178748;
      width: 140px;
      text-align: center;
      padding: 8px;
      border-radius: 0 0 10px 10px;
      color: #fff;
      height: 36px;
    }

    &.d2c-quote {
      .v-btn {
        color: #106175;
      }
    }
  }
}

.cheapest-flag__container {
  border-top: 1px solid #ddd;
}

.view-more-price__wrapper {
  text-align: center;
  margin-top: 35px;
  margin-bottom: 25px;

  a {
    gap: 8px;
    cursor: pointer;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.4px;
    text-align: center;
    color: #106175;
    user-select: none;
    display: inline-flex;
    align-items: center;
    letter-spacing: 0;

    span {
      letter-spacing: 0.4px;
    }

    i {
      color: #106175;
      font-size: 20px;
    }
  }
}

@media screen and (min-width: $grid-breakpoints-lg) {
  .d2c-quotation-result__container {
    .cheapest-flag__container {
      width: 1016px;
      margin: 41px 0 11px -24px;
      height: auto;
      padding-left: 0px !important;
    }

    .section-title {
      padding-left: 8px;
    }
  }
}
</style>
